import type { ChartData, ClickPoint, PathSegment } from '../types';
import type { ReplayFrame } from '../lib/utils';
import { isModActive } from '../mods.js';
import { samplesToPath } from './pathLinker';
import {
  buildPathTable,
  generateTicks,
  decodePathCommands,
  tessellateCommands,
} from './pathLinker';
import {
  coalesceSliderChains,
  assertNoConsecutiveSliders,
  assertNoDegenerateSliders,
} from './coalesceSliderChains';

interface TimingPoint {
  time: number;
  bpm: number;
  meter: number;
}

// Stage dimensions must match Stage9x16 (900x1600) used throughout the maker
// and Beatstage player. Previously these values were 540x960 which corresponded
// to an old stage size. Using the smaller dimensions caused replay frames to be
// scaled to a much smaller coordinate space, resulting in notes being rendered
// at the origin and perfect replays missing during preview. Aligning the values
// here ensures generated replay coordinates line up with rendered hit objects.
const STAGE_WIDTH = 900;
const STAGE_HEIGHT = 1600;
const FRAME_EPS = 1; // ms - send replay events slightly early

function toPixels(x: number, y: number, hr: boolean): { x: number; y: number } {
  const py = hr ? 16 - y : y;
  return {
    x: (x / 9) * STAGE_WIDTH,
    y: (py / 16) * STAGE_HEIGHT,
  };
}

function inSegment(t: number, seg: PathSegment): boolean {
  return t >= seg.startMs && t <= seg.endMs;
}

/**
 * Build a deterministic replay that achieves perfect 300 judgements for the
 * provided difficulty data. The replay honours time/position modifying mods
 * such as Hard Rock and Double/ Half Time via the global mod configuration.
 */
function buildPerfectReplay(
  difficulty: ChartData,
  timing: TimingPoint[],
  hitObjects?: any[],
): ReplayFrame[] {
  const frames: ReplayFrame[] = [];
  const hr = isModActive('hardRock');

  const notes = hitObjects ?? buildHitObjects(difficulty, timing);

  for (const note of notes) {
    if (note.type === 'spinner') {
      const center = toPixels(note.pos.x, note.pos.y, hr);
      const duration = note.tEnd - note.tStart;
      const radius = 30;
      const required = Math.PI * 2 * (duration / 1000);
      const spins = required * 1.1;
      const steps = Math.max(16, Math.ceil(duration / 25));
      for (let i = 0; i <= steps; i++) {
        const t = Math.max(note.tStart + (duration * i) / steps - FRAME_EPS, 0);
        const angle = (spins * i) / steps;
        const x = center.x + radius * Math.cos(angle);
        const y = center.y + radius * Math.sin(angle);
        frames.push({ t, x, y, buttons: ['left'] });
      }
      frames.push({ t: note.tEnd + FRAME_EPS, x: center.x, y: center.y, buttons: [] });
      continue;
    }

    if (note.type === 'slider') {
      // Prefer the encoded path when present so merged sliders maintain their
      // curves even if point arrays were omitted during serialization.
      let points: { x: number; y: number }[] = [];
      if (Array.isArray(note.path)) {
        const cmds = decodePathCommands(note.path);
        if (!cmds.some((c) => c.cmd === 'B')) {
          // eslint-disable-next-line no-console
          console.warn('slider path lacks Bézier commands');
        }
        points = tessellateCommands(cmds);
      } else if (Array.isArray(note.points)) {
        points = note.points;
      }

      const table = buildPathTable(points, note.duration, note.repeats ?? 1, true);
      const tickTimes = generateTicks(
        note.tStart,
        note.duration,
        note.beatDivisor ?? 1,
        note.repeats ?? 1,
        timing,
      );

      const startPos = toPixels(points[0].x, points[0].y, hr);
      frames.push({
        t: Math.max(note.tStart - FRAME_EPS, 0),
        x: startPos.x,
        y: startPos.y,
        buttons: ['left'],
      });

      for (let i = 1; i < points.length; i++) {
        const p = toPixels(points[i].x, points[i].y, hr);
        const t = note.tStart + (note.duration * i) / (points.length - 1);
        frames.push({ t: Math.max(t - FRAME_EPS, 0), x: p.x, y: p.y, buttons: ['left'] });
      }

      for (const tt of tickTimes) {
        const p = table.pointAt(tt);
        const px = toPixels(p.x, p.y, hr);
        frames.push({
          t: Math.max(note.tStart + tt - FRAME_EPS, 0),
          x: px.x,
          y: px.y,
          buttons: ['left'],
        });
      }

      const endPos = table.pointAt(note.duration);
      const endPx = toPixels(endPos.x, endPos.y, hr);
      frames.push({
        t: Math.max(note.tEnd - FRAME_EPS, 0),
        x: endPx.x,
        y: endPx.y,
        buttons: ['left'],
      });
      frames.push({ t: note.tEnd + FRAME_EPS, x: endPx.x, y: endPx.y, buttons: [] });
      continue;
    }

    if (note.type === 'circle') {
      const pos = toPixels(note.pos.x, note.pos.y, hr);
      frames.push({ t: Math.max(note.tStart - FRAME_EPS, 0), x: pos.x, y: pos.y, buttons: ['left'] });
      frames.push({ t: note.tStart + FRAME_EPS, x: pos.x, y: pos.y, buttons: [] });
    }
  }

  frames.sort((a, b) => a.t - b.t);
  return frames;
}

export function buildHitObjects(
  difficulty: ChartData,
  timing: TimingPoint[],
): any[] {
  const hitObjects: any[] = [];
  const usedPoints = new Set<ClickPoint>();

  const bpmAt = (time: number) => {
    let current = timing[0]?.bpm ?? 120;
    for (const tp of timing) {
      if (time >= tp.time) current = tp.bpm; else break;
    }
    return current;
  };

  for (const seg of difficulty.paths) {
    const pts = seg.points.map((p) => ({ x: p.x, y: p.y, tMs: p.tMs }));
    const duration = seg.endMs - seg.startMs;
    let dist = 0;
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1];
      const b = pts[i];
      dist += Math.hypot(b.x - a.x, b.y - a.y);
    }

    if (dist < 0.01 && duration >= 1000) {
      hitObjects.push({
        type: 'spinner',
        tStart: seg.startMs,
        tEnd: seg.endMs,
        duration,
        pos: { x: pts[0].x, y: pts[0].y },
        approachRate: 7,
      });
    } else if (dist <= 0.5) {
      // Very short paths behave like circles and would otherwise trigger the
      // degenerate slider guard. Treat them as circles to avoid preview errors
      // while still keeping their timing information.
      hitObjects.push({
        type: 'circle',
        tStart: seg.startMs,
        pos: { x: pts[0].x, y: pts[0].y },
        approachRate: 7,
      });
    } else {
      const { path, repeats, points } = samplesToPath(pts);
      let pixelLength = 0;
      for (let i = 1; i < points.length; i++) {
        const a = points[i - 1];
        const b = points[i];
        pixelLength += Math.hypot(b.x - a.x, b.y - a.y);
      }
      pixelLength *= 100;
      const beatDuration = 60000 / bpmAt(seg.startMs);
      const beats = duration / beatDuration;
      const sv = beats > 0 ? pixelLength / (100 * beats) : 1;
      hitObjects.push({
        type: 'slider',
        tStart: seg.startMs,
        tEnd: seg.endMs,
        duration,
        pos: { x: pts[0].x, y: pts[0].y },
        approachRate: 7,
        path,
        points,
        repeats,
        pixelLength,
        sv,
        beatDivisor: 1,
      });
    }

    for (const cp of difficulty.clickPoints) {
      if (inSegment(cp.tMs, seg)) usedPoints.add(cp);
    }
  }

  for (const cp of difficulty.clickPoints) {
    if (usedPoints.has(cp)) continue;
    hitObjects.push({
      type: 'circle',
      tStart: cp.tMs,
      pos: { x: cp.x, y: cp.y },
      approachRate: 7,
    });
  }

  hitObjects.sort((a, b) => a.tStart - b.tStart);
  // Merge consecutive slider segments into single curved sliders so that
  // downstream consumers receive complete slider paths. Clone the merged
  // objects to drop any transient fields and ensure the encoded path survives
  // serialization.
  const merged = coalesceSliderChains(hitObjects);
  assertNoConsecutiveSliders(merged);
  assertNoDegenerateSliders(merged);
  return merged.map((n) => {
    let path = Array.isArray(n.path) ? [...n.path] : undefined;
    if (n.type === 'slider' && path) {
      // Decode the path to ensure Bézier commands survive serialization.
      decodePathCommands(path);
    }
    return { ...n, path };
  });
}

export default buildPerfectReplay;
